import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Rocket, Radio } from "lucide-react";
import { api } from "../lib/api";

function fmtCountdown(net) {
  if (!net) return "T-?";
  const diff = new Date(net).getTime() - Date.now();
  if (diff < 0) return "T+LIVE";
  const s = Math.floor(diff / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600).toString().padStart(2, "0");
  const m = Math.floor((s % 3600) / 60).toString().padStart(2, "0");
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return d > 0 ? `T-${d}d ${h}:${m}:${sec}` : `T-${h}:${m}:${sec}`;
}

/**
 * Global scrolling launch ticker — next 3 upcoming launches with T-countdowns.
 * Sits directly under Nav, always visible. Hidden if backend has no data.
 */
export default function LaunchTicker() {
  const [items, setItems] = useState([]);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/launches/upcoming?limit=3");
        if (!alive) return;
        setItems(r.data?.results || []);
      } catch { /* silent */ }
    };
    load();
    const refresh = setInterval(load, 60_000);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => { alive = false; clearInterval(refresh); clearInterval(tick); };
  }, []);

  if (!items.length) return null;

  const cells = items.map(l => ({
    id: l.id,
    net: l.net,
    provider: l.launch_service_provider?.name || l.launch_service_provider || "—",
    name: l.name || "Unknown mission",
    pad: l.pad?.name || l.pad?.location?.name || "—",
  }));

  // Duplicate array so the marquee wraps seamlessly.
  const loop = [...cells, ...cells];

  return (
    <div
      data-testid="launch-ticker"
      className="border-b border-[color:var(--sat-border)] bg-[color:var(--sat-surface)] overflow-hidden relative"
      aria-label="Upcoming launches ticker"
    >
      <div className="max-w-[1400px] mx-auto flex items-stretch">
        <div className="flex items-center gap-1.5 px-3 border-r border-[color:var(--sat-border)] bg-black">
          <Radio size={11} className="sat-cyan animate-pulse" />
          <span className="sat-label sat-cyan text-[9px]">NEXT UP</span>
        </div>
        <div className="flex-1 overflow-hidden">
          <div className="sat-ticker-track py-1.5 flex items-center gap-8 whitespace-nowrap" data-now={now}>
            {loop.map((c, i) => (
              <Link
                key={`${c.id}-${i}`}
                to={`/launches/${c.id}`}
                data-testid={i < cells.length ? `ticker-launch-${i}` : undefined}
                className="flex items-center gap-2 font-mono text-[11px] hover:sat-cyan transition-colors"
              >
                <Rocket size={11} className="sat-cyan" />
                <span className="sat-cyan font-bold">{fmtCountdown(c.net)}</span>
                <span className="text-[color:var(--sat-text-dim)]">·</span>
                <span className="text-[color:var(--sat-text)]">{c.provider}</span>
                <span className="text-[color:var(--sat-text-dim)]">·</span>
                <span className="text-[color:var(--sat-text-dim)]">{c.name}</span>
                <span className="text-[color:var(--sat-text-dim)]">·</span>
                <span className="text-[color:var(--sat-text-dim)]">{c.pad}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
